import { Plus, Trash2, Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby, Shield, Droplet, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ProductDescriptionEditor from "@/components/ProductDescriptionEditor";
import { mediaUrl, extractErr } from "@/lib/api";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { useState } from "react";

export const COSMOS_ICONS = {
  Sparkles, Leaf, Heart, Award, Clock, ShieldCheck, Star, Droplets, Sun, Smile, Zap, BadgeCheck, Flower2, Baby, Shield, Droplet,
};

export const EMPTY_COSMOS_DESIGN = {
  bestseller_tags: [],
  card_badge: { text: "", bg_color: "#16A34A", text_color: "#FFFFFF" },
  creative_info: [],
  why_choose_us: "",
  ingredients: [],
  how_to_use: [],
  additional_info: [],
  faqs: [],
};

const TAG_BG = "#DC2626";
const TAG_TEXT = "#FFFFFF";

function asTag(tag) {
  if (typeof tag === "string") return { text: tag, bg_color: TAG_BG, text_color: TAG_TEXT };
  return {
    text: tag?.text || "",
    bg_color: /^#[0-9A-Fa-f]{6}$/.test(tag?.bg_color || "") ? tag.bg_color : TAG_BG,
    text_color: /^#[0-9A-Fa-f]{6}$/.test(tag?.text_color || "") ? tag.text_color : TAG_TEXT,
  };
}

function ColorField({ label, value, onChange, testId }) {
  const hex = /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#000000";
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-600">
      {label}
      <input type="color" data-testid={testId} value={hex} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5" />
      <span className="font-mono-nums">{hex.toUpperCase()}</span>
    </label>
  );
}

function patchList(list, idx, row) {
  const next = [...list];
  next[idx] = row;
  return next;
}

function IconPick({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {Object.entries(COSMOS_ICONS).map(([name, Ic]) => (
        <button key={name} type="button" aria-label={name} title={name} onClick={() => onChange(name)} className={`w-7 h-7 rounded-md border flex items-center justify-center ${value === name ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
          <Ic className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

function IngredientImage({ url, onChange, testId }) {
  const uploadFile = useFileUpload();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "product");
    setBusy(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center">
        {url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">Image</span>}
      </div>
      <label className="text-xs text-blue-600 cursor-pointer" data-testid={testId}>
        <Upload className="w-3.5 h-3.5 inline mr-1" />{busy ? "Uploading…" : (url ? "Replace" : "Upload")}
        <input type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {url ? <button type="button" className="text-xs text-red-600" onClick={() => onChange("")}>Remove</button> : null}
    </div>
  );
}

export default function CosmosDesignFields({ value, onChange }) {
  const design = { ...EMPTY_COSMOS_DESIGN, ...(value || {}) };
  const set = (patch) => onChange({ ...design, ...patch });
  const tags = (design.bestseller_tags || []).map(asTag);
  const cardBadge = {
    text: design.card_badge?.text || "",
    bg_color: /^#[0-9A-Fa-f]{6}$/.test(design.card_badge?.bg_color || "") ? design.card_badge.bg_color : "#16A34A",
    text_color: /^#[0-9A-Fa-f]{6}$/.test(design.card_badge?.text_color || "") ? design.card_badge.text_color : "#FFFFFF",
  };
  const groups = design.creative_info || [];
  const ingredients = design.ingredients || [];
  const steps = design.how_to_use || [];
  const extra = design.additional_info || [];
  const faqs = design.faqs || [];

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 space-y-6" data-testid="cosmos-design-fields">
      <div>
        <div className="font-semibold text-slate-900">Cosmos Design Enhancer</div>
        <p className="text-xs text-slate-500 mt-1">These blocks show on the Cosmos product page while the addon is on.</p>
      </div>

      <div className="space-y-2" data-testid="cosmos-bestseller-tags">
        <Label>Bestseller tags</Label>
        <p className="text-xs text-slate-500">Up to 3. Each tag has its own background and text color, and shows above the product title.</p>
        <div className="flex flex-wrap gap-2">
          {tags.filter((tag) => tag.text).map((tag, idx) => (
            <span key={`${tag.text}-${idx}`} className="inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide" style={{ background: tag.bg_color, color: tag.text_color }}>{tag.text}</span>
          ))}
        </div>
        {tags.map((tag, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`cosmos-tag-row-${idx}`}>
            <div className="flex gap-2">
              <Input data-testid={`cosmos-tag-${idx}`} maxLength={40} value={tag.text} onChange={(e) => set({ bestseller_tags: patchList(tags, idx, { ...tag, text: e.target.value }) })} />
              <Button type="button" variant="ghost" aria-label="Remove tag" onClick={() => set({ bestseller_tags: tags.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-4">
              <ColorField label="Background" value={tag.bg_color} testId={`cosmos-tag-bg-${idx}`} onChange={(bg_color) => set({ bestseller_tags: patchList(tags, idx, { ...tag, bg_color }) })} />
              <ColorField label="Text" value={tag.text_color} testId={`cosmos-tag-fg-${idx}`} onChange={(text_color) => set({ bestseller_tags: patchList(tags, idx, { ...tag, text_color }) })} />
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-tag-add" disabled={tags.length >= 3} onClick={() => set({ bestseller_tags: [...tags, { text: "", bg_color: TAG_BG, text_color: TAG_TEXT }] })}><Plus className="w-4 h-4 mr-1" /> Add tag</Button>
      </div>

      <div className="space-y-2" data-testid="cosmos-badge-editor">
        <Label>Badge Editor</Label>
        <p className="text-xs text-slate-500">One badge for this product. It replaces the badge already on the product card, such as Best Seller.</p>
        {cardBadge.text ? (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: cardBadge.bg_color, color: cardBadge.text_color }}>{cardBadge.text}</span>
        ) : null}
        <Input data-testid="cosmos-card-badge-text" maxLength={32} placeholder="Best Seller" value={cardBadge.text} onChange={(e) => set({ card_badge: { ...cardBadge, text: e.target.value } })} />
        <div className="flex flex-wrap gap-4">
          <ColorField label="Background" value={cardBadge.bg_color} testId="cosmos-card-badge-bg" onChange={(bg_color) => set({ card_badge: { ...cardBadge, bg_color } })} />
          <ColorField label="Text" value={cardBadge.text_color} testId="cosmos-card-badge-fg" onChange={(text_color) => set({ card_badge: { ...cardBadge, text_color } })} />
        </div>
      </div>

      <div className="space-y-3" data-testid="cosmos-creative-info">
        <Label>Creative info</Label>
        <p className="text-xs text-slate-500">Each row has a title, like Helps or Targets, and chips with an icon and description.</p>
        {groups.map((group, gi) => (
          <div key={gi} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`cosmos-creative-${gi}`}>
            <div className="flex gap-2">
              <Input data-testid={`cosmos-creative-title-${gi}`} maxLength={24} placeholder="Helps" value={group.title || ""} onChange={(e) => set({ creative_info: patchList(groups, gi, { ...group, title: e.target.value }) })} />
              <select
                className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"
                data-testid={`cosmos-creative-tone-${gi}`}
                value={group.tone || "outline"}
                onChange={(e) => set({ creative_info: patchList(groups, gi, { ...group, tone: e.target.value }) })}
              >
                <option value="outline">Outline chips</option>
                <option value="solid">Solid chips</option>
              </select>
              <Button type="button" variant="ghost" aria-label="Remove row" onClick={() => set({ creative_info: groups.filter((_, i) => i !== gi) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
            {(group.items || []).map((item, ii) => (
              <div key={ii} className="rounded-md border border-slate-100 p-2 space-y-2">
                <div className="flex gap-2">
                  <Input data-testid={`cosmos-creative-text-${gi}-${ii}`} maxLength={48} placeholder="Intense Hydration" value={item.text || ""} onChange={(e) => {
                    const items = patchList(group.items || [], ii, { ...item, text: e.target.value });
                    set({ creative_info: patchList(groups, gi, { ...group, items }) });
                  }} />
                  <Button type="button" variant="ghost" aria-label="Remove chip" onClick={() => {
                    const items = (group.items || []).filter((_, i) => i !== ii);
                    set({ creative_info: patchList(groups, gi, { ...group, items }) });
                  }}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <IconPick value={item.icon || "Sparkles"} testId={`cosmos-creative-icon-${gi}-${ii}`} onChange={(icon) => {
                  const items = patchList(group.items || [], ii, { ...item, icon });
                  set({ creative_info: patchList(groups, gi, { ...group, items }) });
                }} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid={`cosmos-creative-add-item-${gi}`} disabled={(group.items || []).length >= 8} onClick={() => {
              const items = [...(group.items || []), { text: "", icon: "Droplets" }];
              set({ creative_info: patchList(groups, gi, { ...group, items }) });
            }}><Plus className="w-3.5 h-3.5 mr-1" /> Add chip</Button>
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-creative-add" disabled={groups.length >= 6} onClick={() => set({ creative_info: [...groups, { title: "", tone: groups.length % 2 ? "solid" : "outline", items: [{ text: "", icon: "Droplets" }] }] })}><Plus className="w-4 h-4 mr-1" /> Add info row</Button>
      </div>

      <div data-testid="cosmos-why">
        <Label>Why Choose Us?</Label>
        <div className="mt-1">
          <ProductDescriptionEditor value={design.why_choose_us || ""} testId="cosmos-why-editor" onChange={(html) => set({ why_choose_us: html })} />
        </div>
      </div>

      <div className="space-y-2" data-testid="cosmos-ingredients">
        <Label>Ingredients List</Label>
        {ingredients.map((row, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`cosmos-ingredient-${idx}`}>
            <div className="flex justify-end">
              <button type="button" className="text-slate-400 hover:text-red-600" aria-label="Remove ingredient" onClick={() => set({ ingredients: ingredients.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></button>
            </div>
            <IngredientImage url={row.image_url} testId={`cosmos-ingredient-image-${idx}`} onChange={(image_url) => set({ ingredients: patchList(ingredients, idx, { ...row, image_url }) })} />
            <Input data-testid={`cosmos-ingredient-name-${idx}`} maxLength={40} placeholder="Azelaic Acid" value={row.name || ""} onChange={(e) => set({ ingredients: patchList(ingredients, idx, { ...row, name: e.target.value }) })} />
            <Input data-testid={`cosmos-ingredient-text-${idx}`} maxLength={120} placeholder="Reduces bacteria and fades post-acne marks" value={row.text || ""} onChange={(e) => set({ ingredients: patchList(ingredients, idx, { ...row, text: e.target.value }) })} />
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-ingredient-add" disabled={ingredients.length >= 12} onClick={() => set({ ingredients: [...ingredients, { name: "", text: "", image_url: "" }] })}><Plus className="w-4 h-4 mr-1" /> Add ingredient</Button>
      </div>

      <div className="space-y-2" data-testid="cosmos-howto">
        <Label>How To Use</Label>
        {steps.map((row, idx) => (
          <div key={idx} className="flex gap-2" data-testid={`cosmos-step-${idx}`}>
            <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
            <div className="flex-1 space-y-1">
              <Input data-testid={`cosmos-step-title-${idx}`} maxLength={32} placeholder="Target" value={row.title || ""} onChange={(e) => set({ how_to_use: patchList(steps, idx, { ...row, title: e.target.value }) })} />
              <Input data-testid={`cosmos-step-text-${idx}`} maxLength={240} placeholder="Apply a small amount directly onto the pimple." value={row.text || ""} onChange={(e) => set({ how_to_use: patchList(steps, idx, { ...row, text: e.target.value }) })} />
            </div>
            <Button type="button" variant="ghost" aria-label="Remove step" onClick={() => set({ how_to_use: steps.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-step-add" disabled={steps.length >= 8} onClick={() => set({ how_to_use: [...steps, { title: "", text: "" }] })}><Plus className="w-4 h-4 mr-1" /> Add step</Button>
      </div>

      <div className="space-y-2" data-testid="cosmos-additional">
        <Label>Additional Information</Label>
        {extra.map((row, idx) => (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2" data-testid={`cosmos-extra-${idx}`}>
            <Input data-testid={`cosmos-extra-label-${idx}`} maxLength={40} placeholder="Best Before" value={row.label || ""} onChange={(e) => set({ additional_info: patchList(extra, idx, { ...row, label: e.target.value }) })} />
            <Textarea data-testid={`cosmos-extra-value-${idx}`} rows={2} maxLength={400} placeholder="24 Months" value={row.value || ""} onChange={(e) => set({ additional_info: patchList(extra, idx, { ...row, value: e.target.value }) })} />
            <Button type="button" variant="ghost" aria-label="Remove detail" onClick={() => set({ additional_info: extra.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-extra-add" disabled={extra.length >= 12} onClick={() => set({ additional_info: [...extra, { label: "", value: "" }] })}><Plus className="w-4 h-4 mr-1" /> Add detail</Button>
      </div>

      <div className="space-y-2" data-testid="cosmos-faqs">
        <Label>Product FAQ</Label>
        <p className="text-xs text-slate-500">Shown under the description tabs. Each product keeps its own questions.</p>
        {faqs.map((row, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`cosmos-faq-${idx}`}>
            <div className="flex gap-2">
              <Input data-testid={`cosmos-faq-q-${idx}`} maxLength={180} placeholder="How quickly does it work?" value={row.question || ""} onChange={(e) => set({ faqs: patchList(faqs, idx, { ...row, question: e.target.value }) })} />
              <Button type="button" variant="ghost" aria-label="Remove question" onClick={() => set({ faqs: faqs.filter((_, i) => i !== idx) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Textarea data-testid={`cosmos-faq-a-${idx}`} rows={3} maxLength={1200} placeholder="A visible reduction in redness may be noticed within hours." value={row.answer || ""} onChange={(e) => set({ faqs: patchList(faqs, idx, { ...row, answer: e.target.value }) })} />
          </div>
        ))}
        <Button type="button" variant="outline" data-testid="cosmos-faq-add" disabled={faqs.length >= 12} onClick={() => set({ faqs: [...faqs, { question: "", answer: "" }] })}><Plus className="w-4 h-4 mr-1" /> Add question</Button>
      </div>
    </div>
  );
}
